import moment, { Moment } from "moment";
import { IPolygon } from "../../../Models/Maps/IPolygon";

export type ReportRange = [Date, Date];
export const datePickerFormat = "MM-DD-YYYY";
export const timePickerFormat = "h:mm A";
export const earliestReportYearValue = 2019;
export const daysAgoDataBegins = 2;
export const limitMilesOptions = ["Greater than", "Less than"];

export const isMobilyticsPlace = (bucketType: number) => {
    return (
        bucketType
      === (window as any).scout.models.DataBucketTypeEnum.MOBILYTICS_PLACE
    || bucketType
      === (window as any).scout.models.DataBucketTypeEnum.MOBILYTICS_PLACE_GEOFENCE
    );
};

export interface IMobilyticsScenarioFormState {
  id: string;
  title: string;
  startDateTime: Date;
  endDateTime: Date;
  allDayChecked: boolean;
  mileLimitChecked: boolean;
  milesLessGreater: string;
  miles: number;
  polygon: IPolygon;
  isCustomPolygon: boolean;
  address?: string;
  brandId?: string;
  lat?: number;
  lng?: number;
  locationId?: string;
  placeId: string;
}

export interface IMobilyticsScenarioFormProps
  extends IMobilyticsScenarioFormState {
  index: number;
  loading: boolean;
  fillColorHex: string;
  fillOpacity: number;
  canDuplicate: boolean;
  multipleScenarios?: boolean;
  logo: string;
  updateDates: (
    scenarioIndex: number,
    dates: [Moment, Moment],
    dateStrings: [string, string]
  ) => void;
  updateStartTime: (
    scenarioIndex: number,
    time: Moment,
    timeString: string
  ) => void;
  updateEndTime: (
    scenarioIndex: number,
    time: Moment,
    timeString: string
  ) => void;
  toggleAllDayChecked: (scenarioIndex: number) => void;
  toggleMileLimitChecked: (scenarioIndex: number) => void;
  onLessGreaterChange: (scenarioIndex: number, value: string) => void;
  onMilesChange: (
    scenarioIndex: number,
    value: string
  ) => void;
  onDuplicateClick: (index: number) => void;
  onRemoveClick: (index: number) => void;
  onEditTitleSubmit: (index: number, title: string) => void;
}

export enum TabSelection {
  Browse = "Browse",
  Selected = "Selected",
}

export enum HouseholdsFilter {
  LessThan = "Less than",
  MoreThan = "More than",
}

export enum ReportTime {
  Start,
  End,
}

const removeTime = (date: Date) => {
    const newDate = new Date(date);
    newDate.setHours(0);
    newDate.setMinutes(0);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);

    return newDate;
};

export const minDate = () => {
    let from = new Date();
    from.setFullYear(earliestReportYearValue);
    from.setMonth(0);
    from.setDate(1);
    from = removeTime(from);

    return from;
};

export const maxDate = () => {
    return moment()
        .subtract(daysAgoDataBegins, 'days')
        .hours(17)
        .minutes(0)
        .seconds(0)
        .toDate();
};

const defaultStartDate = () => {
    return moment(maxDate())
        .subtract(1, 'years')
        .hours(8)
        .toDate();
};

const defaultEndDate = () => {
    return maxDate();
};

export const initialDateValues = (): ReportRange => {
    const from = defaultStartDate();
    const to = defaultEndDate();

    return [from, to];
};

export const defaultScenarioFormState: IMobilyticsScenarioFormState = {
    id: "",
    title: "Default Title",
    startDateTime: initialDateValues()[0],
    endDateTime: initialDateValues()[1],
    allDayChecked: true,
    mileLimitChecked: false,
    milesLessGreater: "GREATER",
    miles: 30,
    polygon: null,
    isCustomPolygon: false,
    placeId: null
};

export const getDisabledDates = (value: moment.Moment) => {
    const min = minDate();
    const max = maxDate();
    const valueNoTime = removeTime(value.toDate());

    return valueNoTime < min || valueNoTime > max;
};

export const getReportNameErrorMessage = (name: string) => {
    let msg = "";
    if (!name || name.length === 0) {
        msg = "Report Name cannot be empty";
    }
    return msg;
};

export const getReportDatesErrorMessage = (values: ReportRange) => {
    if (!values) {
        return "Date range cannot be blank.";
    } else if (values[0].getFullYear() < earliestReportYearValue) {
        return `From Date cannot be less than year ${earliestReportYearValue}.`;
    } else if (values[1] > new Date()) {
        return "To Date cannot be in the future.";
    } else {
        return "";
    }
};

export const getReportTimesErrorMessage = (dates: ReportRange): string => {
    if (dates[0] === null) {
        return "Start time cannot be blank.";
    } else if (dates[1] === null) {
        return "End time cannot be blank.";
    } else {
        return "";
    }
};

export const isDateDisabled = (date: Moment): boolean => {
    return date < moment(minDate()) || date > moment(maxDate());
}

export const buildDateTime = (date: Moment, time: Moment) => {
    const tempDate = moment(date);
    return tempDate
        .hours(time.hour())
        .minutes(time.minute())
        .seconds(time.second())
        .toDate();
};
